import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

function ListingCard({ listing }) {
  const [hovered, setHovered] = useState(false);

  const thumbnailUrl =
    listing.images?.[listing.thumbnailIndex || 0]?.thumbnail ||
    listing.images?.[0]?.thumbnail ||
    listing.imageUrl ||
    'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&q=70';

  const priceLabel =
    listing.category === 'stays'
      ? `$${listing.price} / ${listing.priceType || 'night'}`
      : `$${listing.price}`;

  return (
    <Link to={`/listing/${listing.id}`} style={{ textDecoration: 'none' }}>
      <article
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
      >
        <div style={{ borderRadius: 12, overflow: 'hidden', aspectRatio: '1 / 1', position: 'relative' }}>
          <img
            src={thumbnailUrl}
            alt={listing.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: hovered ? 'rgba(17,17,16,0.04)' : 'transparent' }} />
        </div>
        <div style={{ paddingTop: 10 }}>
          <p style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 13, fontWeight: 600, color: '#111110', lineHeight: 1.3, margin: 0 }}>
            {listing.title}
          </p>
          <p style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 12, color: '#888880', margin: '2px 0 4px', lineHeight: 1.3 }}>
            {listing.location}
          </p>
          <p style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 13, color: '#111110', margin: 0 }}>
            <strong>{priceLabel}</strong>
          </p>
        </div>
      </article>
    </Link>
  );
}

function Home() {
  const [stayListings, setStayListings] = useState([]);
  const [marketplaceListings, setMarketplaceListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFeatured = async () => {
      setLoading(true);
      setError('');
      try {
        const listingsRef = collection(db, 'listings');
        const [staysSnapshot, marketplaceSnapshot] = await Promise.all([
          getDocs(query(listingsRef, where('category', '==', 'stays'))),
          getDocs(query(listingsRef, where('category', '==', 'marketplace'))),
        ]);

        const sort = (docs) =>
          docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
            .slice(0, 4);

        setStayListings(sort(staysSnapshot.docs));
        setMarketplaceListings(sort(marketplaceSnapshot.docs));
      } catch (fetchError) {
        console.error('Home fetch error', fetchError);
        setError('Unable to load featured listings. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchFeatured();
  }, []);

  const emptyMsg = (msg) => (
    <p style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 14, color: '#555553', textAlign: 'center', marginTop: '1rem' }}>
      {msg}
    </p>
  );

  return (
    <main style={{ minHeight: '100vh', background: '#F7F5F0' }}>

      {/* ── Hero: split layout ── */}
      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '2rem 2rem 0' }}>
        <div style={{
          borderRadius: 20,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          minHeight: 440,
          background: '#fff',
          boxShadow: '0 18px 60px rgba(17,17,16,0.08)',
        }}>
          {/* Left: text */}
          <div style={{ padding: '3.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h1 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '2.6rem', lineHeight: 1.1, color: '#111110', fontWeight: 400, margin: 0 }}>
              Find your next stay or travel gear in one place.
            </h1>
            <p style={{ marginTop: '1.25rem', fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '0.95rem', color: '#555553', lineHeight: 1.75, maxWidth: 380 }}>
              Hostel stays, private rooms, sublets, and secondhand travel essentials — from local hosts and fellow travelers.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem', flexWrap: 'wrap' }}>
              <Link
                to="/stays"
                style={{ padding: '12px 22px', borderRadius: 8, border: 'none', background: '#4A9E8A', color: '#F7F5F0', fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 14, fontWeight: 500, textDecoration: 'none' }}
              >
                Browse stays
              </Link>
              <Link
                to="/marketplace"
                style={{ padding: '12px 22px', borderRadius: 8, border: '1px solid #E0DDD6', background: '#fff', color: '#111110', fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 14, textDecoration: 'none' }}
              >
                Browse marketplace
              </Link>
            </div>
          </div>
          {/* Right: photo */}
          <div style={{ position: 'relative', overflow: 'hidden', minHeight: 400 }}>
            <img
              src="/img-home.jpg"
              alt="A bright hostel common area"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg, rgba(247,245,240,0.18) 0%, transparent 60%)' }} />
          </div>
        </div>
      </section>

      {/* ── Featured stays ── */}
      <section style={{ maxWidth: 1180, margin: '2.5rem auto 0', padding: '0 2rem 2rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '1.4rem', color: '#111110', fontWeight: 400, margin: 0 }}>
            Featured stays
          </h2>
          <Link to="/stays" style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 13, color: '#4A9E8A', textDecoration: 'underline', textUnderlineOffset: 3 }}>
            Show all
          </Link>
        </div>
        {loading ? emptyMsg('Loading featured stays…') : stayListings.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.25rem' }}>
            {stayListings.map((listing) => <ListingCard key={listing.id} listing={listing} />)}
          </div>
        ) : emptyMsg('No stay listings are available yet.')}
      </section>

      {/* ── Featured marketplace ── */}
      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '0 2rem 3rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '1.4rem', color: '#111110', fontWeight: 400, margin: 0 }}>
            Featured marketplace
          </h2>
          <Link to="/marketplace" style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 13, color: '#4A9E8A', textDecoration: 'underline', textUnderlineOffset: 3 }}>
            Show all
          </Link>
        </div>
        {loading ? emptyMsg('Loading featured marketplace listings…') : marketplaceListings.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.25rem' }}>
            {marketplaceListings.map((listing) => <ListingCard key={listing.id} listing={listing} />)}
          </div>
        ) : emptyMsg('No marketplace listings are available yet.')}
      </section>

      {error && (
        <p style={{ fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: 14, color: '#E24B4A', textAlign: 'center', paddingBottom: '1rem' }}>
          {error}
        </p>
      )}
    </main>
  );
}

export default Home;
